<script setup>
import { computed, ref, watch } from 'vue'
import { router } from '@inertiajs/vue3'
import { useToast } from 'primevue/usetoast'
import { useI18n } from 'vue-i18n'
import { COUNTY_MODES, QUALIFIED_COUNTY_MODES, countyModeKey } from '@/constants/countyAccess'

const props = defineProps({
  visible: { type: Boolean, default: false },
  // Company row incl. county_access relation (one row per county + mode)
  // from the admin companies prop.
  company: { type: Object, default: null },
})

const emit = defineEmits(['update:visible'])

const { t } = useI18n()
const toast = useToast()

const dialogVisible = computed({
  get: () => props.visible,
  set: (value) => emit('update:visible', value),
})

// One row per (county, mode) the county offers; the key is the row's identity,
// qualified mirrors the backend pin's subject qualifier (matrixFor's flag).
const rows = computed(() =>
  Object.entries(COUNTY_MODES).flatMap(([county, modes]) =>
    modes.map((mode) => {
      const key = countyModeKey(county, mode)
      return { county, mode, key, qualified: QUALIFIED_COUNTY_MODES.has(key) }
    }),
  ),
)

// Optimistic per-row enabled state, seeded from the relation and re-seeded
// whenever a different company opens the dialog.
const enabledByKey = ref({})
const savingKey = ref(null)

watch(
  () => [props.visible, props.company?.id],
  () => {
    if (!props.company) return
    const enabled = new Set(
      (props.company.county_access ?? []).map((row) => countyModeKey(row.county, row.mode)),
    )
    enabledByKey.value = Object.fromEntries(rows.value.map((row) => [row.key, enabled.has(row.key)]))
  },
  { immediate: true },
)

const enabledCount = computed(() => Object.values(enabledByKey.value).filter(Boolean).length)

const modeLabel = (mode) => {
  if (mode === 'comparables') return t('messages.admin.countyAccess.modeComparables')
  if (mode === 'direct') return t('messages.admin.countyAccess.modeDirect')
  return t('messages.admin.countyAccess.modeNone')
}

const onToggle = (row, enabled) => {
  if (!props.company) return

  const previous = enabledByKey.value[row.key]
  enabledByKey.value = { ...enabledByKey.value, [row.key]: enabled }
  savingKey.value = row.key

  router.put(
    route('admin.company.county-access', props.company.id),
    { county: row.county, mode: row.mode, enabled },
    {
      preserveScroll: true,
      onSuccess: () => {
        toast.add({
          severity: 'success',
          summary: t('messages.updated'),
          detail: t('messages.admin.countyAccess.updated'),
          life: 3000,
        })
      },
      onError: () => {
        enabledByKey.value = { ...enabledByKey.value, [row.key]: previous }
        toast.add({
          severity: 'error',
          summary: t('messages.error'),
          detail: t('messages.admin.countyAccess.updateError'),
          life: 3000,
        })
      },
      onFinish: () => {
        savingKey.value = null
      },
    },
  )
}
</script>

<template>
  <Dialog
    v-model:visible="dialogVisible"
    modal
    :draggable="false"
    :style="{ width: '30rem' }"
    :header="`${$t('messages.admin.countyAccess.companyTitle')} — ${company?.name ?? ''}`"
  >
    <p class="text-sm text-gray-600 mb-1">
      {{ $t('messages.admin.countyAccess.companyHint') }}
    </p>
    <p class="text-sm text-gray-500 mb-4">
      {{ $t('messages.admin.countyAccess.enabledCount', { count: enabledCount, total: rows.length }) }}
    </p>
    <ul class="divide-y divide-slate-100" data-testid="company-county-list">
      <li
        v-for="row in rows"
        :key="row.key"
        class="flex items-center justify-between py-2"
        :data-county="row.county"
        :data-mode="row.mode"
      >
        <div class="flex items-center gap-2">
          <span class="text-sm text-gray-800">{{ row.county }}</span>
          <Tag
            :value="modeLabel(row.mode)"
            :severity="row.mode === 'comparables' ? 'info' : 'success'"
            class="text-xs!"
          />
          <span
            v-if="row.qualified"
            v-tooltip.top="$t('messages.admin.countyAccess.qualifiedHint')"
            class="text-xs text-gray-500 cursor-help"
            data-testid="county-mode-qualified"
          >
            ({{ $t('messages.admin.countyAccess.qualified') }})
          </span>
        </div>
        <ToggleSwitch
          :model-value="enabledByKey[row.key] ?? false"
          :disabled="savingKey !== null"
          @update:model-value="onToggle(row, $event)"
        />
      </li>
    </ul>
  </Dialog>
</template>
